import * as React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material'
import { WarningAmber, EventBusy, DeleteForever, EditCalendar } from '@mui/icons-material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import {
  requestDeletePublicEventTrees,
  confirmDeletePublicEventTrees,
} from '../../services'
import VerificationCodeDialog from '../VerificationCodeDialog'

const EventCard = ({ event, onExtend, onCleanDone }) => {
  const [mode, setMode] = React.useState(null) // null | 'extend' | 'confirmClean'
  const [newEndDate, setNewEndDate] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const [otpDialogOpen, setOtpDialogOpen] = React.useState(false)
  const [otpLoading, setOtpLoading] = React.useState(false)
  const [otpError, setOtpError] = React.useState(null)
  const [resendCount, setResendCount] = React.useState(0)
  const [retryAfterSeconds, setRetryAfterSeconds] = React.useState(null)

  const formattedEndDate = new Date(event.endDate).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  const handleExtendConfirm = async () => {
    if (!newEndDate) {
      setError('Selecciona una nueva fecha de fin.')
      return
    }
    if (new Date(newEndDate) <= new Date()) {
      setError('La nueva fecha debe ser futura.')
      return
    }
    setError(null)
    setLoading(true)
    await onExtend(event.id, newEndDate)
    setLoading(false)
  }

  const handleCleanConfirm = async () => {
    setLoading(true)
    setError(null)
    const result = await requestDeletePublicEventTrees(event.id)
    setLoading(false)

    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpDialogOpen(true)
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setMode(null)
      setOtpDialogOpen(true)
    } else {
      setError(result?.message || 'Error al solicitar el código de verificación.')
    }
  }

  const handleRequestNewCode = async () => {
    const result = await requestDeletePublicEventTrees(event.id)
    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpError('Espera antes de reenviar.')
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setOtpError(null)
    }
  }

  const handleOtpConfirm = async code => {
    setOtpLoading(true)
    setOtpError(null)
    const result = await confirmDeletePublicEventTrees(event.id, code)
    setOtpLoading(false)

    if (result?.action === 'deleted' || result?.deleted) {
      setOtpDialogOpen(false)
      onCleanDone()
    } else {
      setOtpError(result?.message || 'Código inválido o expirado.')
    }
  }

  const handleOtpClose = () => {
    if (otpLoading) return
    setOtpDialogOpen(false)
    setOtpError(null)
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'warning.light',
        borderRadius: 2,
        p: 2,
        mb: 2,
        backgroundColor: '#fffde7',
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {event.code}
          </Typography>
          {event.descripcion && (
            <Typography variant="body2" color="text.secondary">
              {event.descripcion}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${event.treeCount} árbol${event.treeCount !== 1 ? 'es' : ''}`}
          size="small"
          color="warning"
          variant="outlined"
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
        <EventBusy fontSize="small" color="error" />
        <Typography variant="caption" color="error.main">
          Finalizó el {formattedEndDate}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mode === 'extend' && (
        <Box sx={{ mb: 1.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Nueva fecha de fin"
              value={newEndDate}
              onChange={setNewEndDate}
              minDateTime={new Date()}
              inputFormat="dd/MM/yyyy HH:mm"
              renderInput={params => (
                <TextField {...params} size="small" fullWidth sx={{ mb: 1 }} />
              )}
            />
          </LocalizationProvider>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              disabled={loading || !newEndDate}
              onClick={handleExtendConfirm}
              startIcon={loading ? <CircularProgress size={14} /> : null}>
              Confirmar fecha
            </Button>
            <Button size="small" onClick={() => { setMode(null); setError(null) }} disabled={loading}>
              Cancelar
            </Button>
          </Box>
        </Box>
      )}

      {mode === 'confirmClean' && (
        <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#ffebee', borderRadius: 1 }}>
          <Typography variant="body2" color="error.dark" sx={{ mb: 1 }}>
            Se eliminarán permanentemente <strong>{event.treeCount}</strong> árbol(es) y todos sus datos asociados. Se enviará un código de verificación a tu correo.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              color="error"
              disabled={loading}
              onClick={handleCleanConfirm}
              startIcon={loading ? <CircularProgress size={14} /> : <DeleteForever fontSize="small" />}>
              Sí, enviar código
            </Button>
            <Button size="small" onClick={() => setMode(null)} disabled={loading}>
              Cancelar
            </Button>
          </Box>
        </Box>
      )}

      {mode === null && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<EditCalendar fontSize="small" />}
            onClick={() => setMode('extend')}>
            Extender fecha
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteForever fontSize="small" />}
            onClick={() => setMode('confirmClean')}>
            Limpiar datos de prueba
          </Button>
        </Box>
      )}

      <VerificationCodeDialog
        open={otpDialogOpen}
        onClose={handleOtpClose}
        onConfirm={handleOtpConfirm}
        onRequestNew={handleRequestNewCode}
        loading={otpLoading}
        resourceLabel={`árboles del evento "${event.code}"`}
        error={otpError}
        resendCount={resendCount}
        retryAfterSeconds={retryAfterSeconds}
      />
    </Box>
  )
}

const EventEndedAlertModal = ({ events, onExtend, onCleanDone, onDismiss }) => {
  if (!events || events.length === 0) return null

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
      onClose={onDismiss}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WarningAmber color="warning" />
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Evento{events.length > 1 ? 's' : ''} de prueba finalizado{events.length > 1 ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Requiere{events.length > 1 ? 'n' : ''} tu atención
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Los siguientes eventos han llegado a su fecha de fin. Los árboles registrados se mantienen hasta que decidas qué hacer con ellos.
        </Typography>

        {events.map(event => (
          <EventCard
            key={event.id}
            event={event}
            onExtend={onExtend}
            onCleanDone={onCleanDone}
          />
        ))}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
          Este aviso reaparecerá mientras haya eventos pendientes de atención.
        </Typography>
        <Button size="small" onClick={onDismiss} color="inherit">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EventEndedAlertModal
